import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { api, type PolicyPack } from '../api/client';
import { usePolicyChat } from '../hooks/usePolicyChat';
import { PolicyChat } from '../components/policy-builder/PolicyChat';
import { PolicyStudio } from '../components/policy-builder/PolicyStudio';

export function Policies() {
  const { currentWorkspaceId } = useAuth();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [applying, setApplying] = useState(false);
  const [applySuccess, setApplySuccess] = useState(false);

  const chat = usePolicyChat(currentWorkspaceId);

  // Load existing workspace policy on mount
  useEffect(() => {
    if (!currentWorkspaceId) return;
    setLoading(true);
    api
      .getPolicies(currentWorkspaceId)
      .then((data) => {
        if (data.is_custom) {
          chat.setCurrentPolicy(data.policy);
        }
      })
      .catch((err) => setLoadError(err.message))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWorkspaceId]);

  if (!currentWorkspaceId) {
    return <p className="text-text-secondary">No workspace found. Complete onboarding first.</p>;
  }

  if (loading) {
    return <p className="text-text-secondary">Loading policies...</p>;
  }

  if (loadError) {
    return (
      <div className="rounded-lg bg-[rgba(248,113,113,0.12)] p-4 text-sm text-[var(--color-error)]">{loadError}</div>
    );
  }

  async function handleApply() {
    if (!currentWorkspaceId || !chat.currentPolicy) return;
    setApplying(true);
    setApplySuccess(false);

    try {
      const validation = await api.validatePolicy(currentWorkspaceId, chat.currentPolicy);
      if (!validation.valid) {
        alert('Validation errors:\n' + validation.errors.join('\n'));
        return;
      }
      await api.updatePolicy(currentWorkspaceId, chat.currentPolicy);
      setApplySuccess(true);
      setTimeout(() => setApplySuccess(false), 3000);
    } catch (err) {
      alert('Failed to apply policy: ' + (err as Error).message);
    } finally {
      setApplying(false);
    }
  }

  return (
    <div className="h-[calc(100vh-8rem)]">
      {/* Success banner */}
      {applySuccess && (
        <div className="mb-2 rounded-lg bg-[rgba(52,211,153,0.12)] px-4 py-2 text-sm text-[var(--color-success)]">
          Policy applied to workspace successfully.
        </div>
      )}

      <div className="grid h-full grid-cols-[1fr_1fr] gap-0 overflow-hidden rounded-xl border border-border-dark">
        {/* Left panel: Chat */}
        <div className="flex min-h-0 flex-col border-r border-border-dark bg-surface-1/50">
          <div className="shrink-0 border-b border-border-dark px-5 py-3">
            <div className="flex items-center gap-2">
              <svg className="h-4 w-4 text-accent-violet" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
              </svg>
              <h2 className="text-sm font-semibold text-text-primary">AI Policy Builder</h2>
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-hidden">
            <PolicyChat
              messages={chat.messages}
              isStreaming={chat.isStreaming}
              error={chat.error}
              onSend={chat.sendMessage}
            />
          </div>
        </div>

        {/* Right panel: Studio */}
        <div className="flex min-h-0 flex-col bg-surface-1/30">
          <PolicyStudio
            policy={chat.currentPolicy}
            onPolicyChange={chat.setCurrentPolicy}
            onApply={handleApply}
            applying={applying}
          />
        </div>
      </div>
    </div>
  );
}
